import React, { useState, useEffect, useCallback } from 'react';
import styled, { keyframes } from 'styled-components';

const API_URL = process.env.REACT_APP_API_URL || 'https://videoreel-api-habwcma9cygwhphm.westus-01.azurewebsites.net';

// Neutral inline placeholder for broken/missing thumbnails (data URI — no network).
const PLACEHOLDER =
  'data:image/svg+xml;utf8,' +
  encodeURIComponent(
    '<svg xmlns="http://www.w3.org/2000/svg" width="80" height="80">' +
      '<rect width="80" height="80" fill="%23191926"/>' +
      '<path d="M20 54l14-16 10 11 8-8 8 13z" fill="%23333349"/>' +
      '<circle cx="30" cy="28" r="6" fill="%23333349"/>' +
    '</svg>'
  );

// ─── Styled components (mirrors AgencyVideoUploadModal Overlay/Modal scaffolding) ──

const fadeIn = keyframes`from { opacity: 0; } to { opacity: 1; }`;
const slideUp = keyframes`from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); }`;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.8);
  backdrop-filter: blur(8px);
  z-index: 10001;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 24px 20px;
  overflow-y: auto;
  animation: ${fadeIn} 150ms ease;
`;

const Modal = styled.div`
  background: linear-gradient(160deg, #0f0f19 0%, #1a0a2e 100%);
  border: 1px solid rgba(255,255,255,0.12);
  border-radius: 20px;
  width: 100%;
  max-width: 620px;
  padding: 28px 24px 24px;
  box-shadow: 0 24px 64px rgba(0,0,0,0.6);
  animation: ${slideUp} 200ms ease;
  color: white;
  margin: auto;
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 20px;
`;

const ModalTitle = styled.h2`
  font-size: 1.15rem;
  font-weight: 700;
  margin: 0;
`;

const CloseBtn = styled.button`
  background: none;
  border: none;
  color: rgba(255,255,255,0.4);
  font-size: 1.4rem;
  cursor: pointer;
  line-height: 1;
  padding: 0;
  &:hover { color: white; }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  gap: 14px;
`;

const Tile = styled.button`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 8px;
  background: #10101c;
  border: 1px solid rgba(255,255,255,0.1);
  border-radius: 12px;
  cursor: pointer;
  text-align: center;
  transition: border-color 0.2s, background 0.2s;
  &:hover { border-color: #ff4757; background: rgba(255,71,87,0.05); }
`;

const TileImg = styled.img`
  width: 100%;
  aspect-ratio: 1 / 1;
  object-fit: contain;
  background: #191926;
  border-radius: 8px;
`;

const TileCaption = styled.span`
  font-size: 0.72rem;
  color: rgba(255,255,255,0.5);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const DeleteTileBtn = styled.button`
  position: absolute;
  top: 4px;
  right: 4px;
  width: 22px;
  height: 22px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0,0,0,0.65);
  border: 1px solid rgba(255,255,255,0.15);
  border-radius: 50%;
  color: rgba(255,255,255,0.7);
  font-size: 0.85rem;
  line-height: 1;
  cursor: pointer;
  opacity: 0;
  transition: opacity 0.15s, background 0.15s, color 0.15s;

  ${Tile}:hover & { opacity: 1; }
  &:hover { background: #ff4757; color: white; border-color: #ff4757; }
  &:disabled { opacity: 0.4; cursor: default; }
`;

const Msg = styled.p`
  color: rgba(255,255,255,0.4);
  font-size: 0.9rem;
  text-align: center;
  padding: 28px 0;
  margin: 0;
`;

const ErrWrap = styled.div`
  text-align: center;
  padding: 24px 0;
`;

const ErrText = styled.p`
  color: #ff6b7a;
  font-size: 0.88rem;
  margin: 0 0 14px;
`;

const RetryBtn = styled.button`
  background: #ff4757;
  color: white;
  border: none;
  border-radius: 10px;
  font-size: 0.85rem;
  font-weight: 600;
  padding: 9px 20px;
  cursor: pointer;
  &:hover { background: #e0404f; }
`;

const Hint = styled.p`
  font-size: 0.72rem;
  color: rgba(255,255,255,0.3);
  margin: 16px 0 0;
  text-align: center;
`;

// ─── Component ─────────────────────────────────────────────────────────────────

const PublisherImageLibraryModal = ({ token, onSelect, onClose }) => {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deletingId, setDeletingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_URL}/api/publisher/images`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`Error ${res.status}`);
      const data = await res.json();
      setImages(data.images || []);
    } catch {
      setError("Couldn't load your images. Try again.");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (e, id) => {
    e.stopPropagation();
    setDeletingId(id);
    try {
      const res = await fetch(`${API_URL}/api/publisher/images/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`Error ${res.status}`);
      setImages(imgs => imgs.filter(img => img.id !== id));
    } catch {
      // Surface a soft failure by reloading the canonical list.
      load();
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <Overlay onClick={onClose}>
      <Modal onClick={e => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>Your images</ModalTitle>
          <CloseBtn onClick={onClose} aria-label="Close">×</CloseBtn>
        </ModalHeader>

        {loading ? (
          <Msg>Loading…</Msg>
        ) : error ? (
          <ErrWrap>
            <ErrText>{error}</ErrText>
            <RetryBtn onClick={load}>Try again</RetryBtn>
          </ErrWrap>
        ) : images.length === 0 ? (
          <Msg>No images yet — upload one to start your library.</Msg>
        ) : (
          <>
            <Grid>
              {images.map(img => (
                <Tile
                  key={img.id}
                  type="button"
                  onClick={() => { onSelect(img.url); onClose(); }}
                  title={img.filename || 'Select image'}
                >
                  <DeleteTileBtn
                    type="button"
                    aria-label={`Delete ${img.filename || 'image'}`}
                    disabled={deletingId === img.id}
                    onClick={e => handleDelete(e, img.id)}
                  >
                    ×
                  </DeleteTileBtn>
                  <TileImg
                    src={img.url}
                    alt={img.filename || 'Uploaded image'}
                    onError={e => { e.currentTarget.src = PLACEHOLDER; }}
                  />
                  <TileCaption>{img.filename || 'image'}</TileCaption>
                </Tile>
              ))}
            </Grid>
            <Hint>Click an image to use it. Deleting here won't change publications already using it.</Hint>
          </>
        )}
      </Modal>
    </Overlay>
  );
};

export default PublisherImageLibraryModal;
